import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FieldValidator,
  FieldValue,
  validateField as vF
} from 'common/forms/validators';
import { Collection } from 'common/module/ui/lazy-modal/types';
import getDisplayName from 'common/tools/react/getDisplayName';
import { getKeys, getValues } from 'common/types';

type Props<P, K extends string> = {
  defaultValues?: { [key in K]?: FieldValue };
  fields: Array<K>;
  validators?: { [key in K]?: FieldValidator<P>[] };
};

type FieldsState<K extends string> = {
  [key in K]?: Field<K>;
};

type FormValues<K extends string> = {
  [key in K]?: FieldValue;
};

type ErrorOrigins = string[];

type FormErrors<K extends string> = {
  [key in K]?: ErrorOrigins;
};

export type WithFormStateProps<K extends string, C = Partial<Collection>> = {
  fieldsState: FieldsState<K>;
  formErrors: FormErrors<K>;
  formValues: C;
  isFormValidating: boolean;
  onFieldBlur: (field: K) => void;
  onFieldChange: (field: K, value: FieldValue) => void;
  validateForm: () => Promise<boolean>;
};

type Field<K extends string> = {
  error: boolean;
  errorOrigins: ErrorOrigins;
  firstStroke: boolean;
  isDirty: boolean;
  name: K;
  validated: boolean;
  validating: boolean;
  value: FieldValue;
};

/**
 *  This HOC will create a form state and transform its child into a controlled
 *  form, allowing it to only reason about how to display the form.
 *
 */
const withFormState =
  <P, K extends string>({
    fields,
    validators = {},
    defaultValues = {}
  }: Props<P, K>) =>
  (WrappedComponent: React.FunctionComponent<P & WithFormStateProps<K>>) => {
    const WithFormState = ({ ...props }: P) => {
      const [fieldsState, setFieldsState] = useState<FieldsState<K>>({});
      const [formErrors, setFormErrors] = useState<FormErrors<K>>({});
      const [formValues, setFormValues] = useState<FormValues<K>>({});
      const [validating, setValidating] = useState(false);
      const fieldsRef = useRef<FieldsState<K>>({});

      const computeFormValues = useCallback(() => {
        const values = getKeys(fieldsState).reduce<FormValues<K>>(
          (values, cur) => ({
            ...values,
            [cur]: fieldsState[cur]?.value
          }),
          {}
        );

        setFormValues(values);
      }, [fieldsState]);

      const computeFormErrors = useCallback(() => {
        const errors = getKeys(fieldsState).reduce<FormErrors<K>>(
          (errors, cur) => {
            const fieldErrors = fieldsState[cur]?.error
              ? fieldsState[cur]?.errorOrigins
              : null;
            return { ...errors, [cur]: fieldErrors };
          },
          {}
        );
        setFormErrors(errors);
      }, [fieldsState]);

      const isFieldBeingValidated = useCallback(
        () => getValues<Field<K>>(fieldsState).some(field => field.validating),
        [fieldsState]
      );

      useEffect(() => {
        if (!isFieldBeingValidated()) {
          computeFormValues();
          computeFormErrors();
        }
      }, [
        computeFormErrors,
        computeFormValues,
        fieldsState,
        isFieldBeingValidated
      ]);

      useEffect(() => {
        fieldsRef.current = fields.reduce<FieldsState<K>>(
          (acc, field) => ({
            ...acc,
            [field]: {
              error: false,
              errorOrigins: null,
              firstStroke: false,
              isDirty: true,
              name: field,
              validated: false,
              validating: false,
              value: defaultValues[field],
              ...(acc[field] ?? {})
            }
          }),
          fieldsRef.current
        );

        setFieldsState(fieldsRef.current);
      }, [fieldsRef]);

      const validateField = useCallback(
        async (field?: Field<K>, force?: boolean) => {
          if (!force && !field?.firstStroke) return;
          if (!force && !field?.isDirty) return !field?.error;

          const fieldName = field?.name;
          if (!fieldName) return false;

          // set field as being validated
          fieldsRef.current = {
            ...fieldsRef.current,
            [fieldName]: { ...field, validating: true, error: false }
          };

          setFieldsState(fieldsRef.current);

          const errorMessages = await vF(
            validators?.[fieldName],
            field.value,
            formValues,
            props
          );

          const fieldHasError = errorMessages && errorMessages.length;
          if (fieldHasError) {
            fieldsRef.current[fieldName] = {
              ...fieldsRef.current[fieldName],
              error: true,
              errorOrigins: [errorMessages[0]],
              validated: false
            };
          } else {
            // Let's see if there is another known error in the form
            fieldsRef.current[fieldName] = {
              ...fieldsRef.current[fieldName],
              validated: true
            };
          }
          fieldsRef.current[fieldName] = {
            ...fieldsRef.current[fieldName],
            validating: false,
            isDirty: false
          };

          setFieldsState(fieldsRef.current);

          return !fieldHasError;
        },
        [formValues, props]
      );

      const onFieldBlur = useCallback(
        (fieldName: K) => validateField(fieldsState[fieldName]),
        [fieldsState, validateField]
      );

      const updateField = useCallback((fieldName: K, value: FieldValue) => {
        fieldsRef.current = {
          ...fieldsRef.current,
          [fieldName]: {
            ...fieldsRef.current[fieldName],
            error: false,
            firstStroke: true,
            isDirty: true,
            value
          }
        };

        setFieldsState(fieldsRef.current);
      }, []);

      // Since we are managing a promise, we must
      // avoid regenerating the function when the state
      // changes.
      const validateForm = useCallback(async () => {
        setValidating(true);
        try {
          const errors = await Promise.all(
            getValues<Field<K>>(fieldsState).map(field =>
              validateField(field, true)
            )
          );
          return !errors.includes(false);
        } catch (e) {
          return false;
        } finally {
          setValidating(false);
        }
      }, [fieldsState, validateField]);

      return (
        <WrappedComponent
          fieldsState={fieldsState}
          formValues={formValues}
          formErrors={formErrors}
          onFieldBlur={onFieldBlur}
          onFieldChange={updateField}
          isFormValidating={validating}
          validateForm={validateForm}
          {...props}
        />
      );
    };

    WithFormState.displayName = `withFormState(${getDisplayName(
      WrappedComponent
    )})`;

    return WithFormState;
  };

export default withFormState;
